import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { OrganizationIdentifierScheme, } from '@debbie/common/dist/sproom/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import * as creditorsAPI from '@debbie/cortex/dist/api/creditors.js';
import { selectExclusiveContent } from '@debbie/cortex/dist/exclusiveContent/selectors.js';
import { selectMe } from '@debbie/cortex/dist/me/selectors.js';
import { Size } from '../design-system.js';
import PlaceholderLoader from './PlaceholderLoader.js';
import SmallHeader from './SmallHeader.js';
import { Checkbox, ExclusiveContent, Input, Select } from './index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import { notification } from '@debbie/cortex/dist/notifications.js';
import { creditorsEntity, useCreditor } from '@debbie/cortex/dist/creditors/index.js';
import keys from '@debbie/common/dist/cache/keys.js';
import q from '@debbie/cortex/dist/query.js';
const CreditorConfig = (props) => {
    const { id } = props;
    const lang = useSelector((s) => s.lang);
    const creditorLoadable = useCreditor(id);
    const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null;
    const me = useSelector(selectMe);
    const exclusiveContent = useSelector(selectExclusiveContent);
    const [loadingRemoveChildCompany, setLoadingRemoveChildCompany] = useState(false);
    const [loadingAddChildCompany, setLoadingAddChildCompany] = useState(false);
    const [organizationIdentifier, setOrganizationIdentifier] = useState({
        scheme: OrganizationIdentifierScheme.DK_CVR,
        value: '',
    });
    const [state, setState] = useState({
        selfCollection: false,
        outboundCall: false,
        autoVerify: false,
        customExpiration: {
            normal: undefined,
            foundation: undefined,
            interest: undefined,
        },
    });
    useEffect(() => {
        if (creditor) {
            setState({
                ...state,
                outboundCall: !!creditor.outboundCall,
                selfCollection: !!creditor.selfCollection,
                autoVerify: !!creditor.autoVerify,
                customExpiration: creditor.settings.customExpiration,
            });
        }
    }, [creditor]);
    const updateSelfCollection = async (selfCollection) => {
        try {
            await creditorsAPI.update(id, {
                selfCollection,
            });
            setState({
                ...state,
                selfCollection,
            });
        }
        catch (error) {
            alert(lang.misc.error + '. ' + lang.misc.contactSupport);
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateOutboundCall = async (outboundCall) => {
        try {
            await creditorsAPI.update(id, {
                outboundCall,
            });
            setState({
                ...state,
                outboundCall,
            });
        }
        catch (error) {
            alert(lang.misc.error + '. ' + lang.misc.contactSupport);
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateAutoVerify = async (autoVerify) => {
        try {
            await creditorsAPI.update(id, {
                autoVerify,
            });
            setState({
                ...state,
                autoVerify,
            });
        }
        catch (error) {
            alert(lang.misc.error + '. ' + lang.misc.contactSupport);
        }
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const updateCustomExpirationNormal = async (_creditor, normal) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                customExpiration: {
                    ..._creditor.settings.customExpiration,
                    normal: normal,
                },
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const customExpirationNormalUpdater = useMutation(updateCustomExpirationNormal, {
        onError: () => {
            notification({
                title: lang.misc.error,
                theme: 'error',
                subtitle: `${lang.misc.errorUpdating} ${lang.creditors.normalExpiration.toLocaleLowerCase()}`,
            });
        },
    });
    const updateCustomExpirationFoundation = async (_creditor, foundation) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                customExpiration: {
                    ..._creditor.settings.customExpiration,
                    foundation: foundation,
                },
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const customExpirationFoundationUpdater = useMutation(updateCustomExpirationFoundation, {
        onError: () => {
            notification({
                title: lang.misc.error,
                theme: 'error',
                subtitle: `${lang.misc.errorUpdating} ${lang.creditors.foundationExpiration.toLocaleLowerCase()}`,
            });
        },
    });
    const updateCustomInterestExpiration = async (_creditor, interest) => {
        await creditorsAPI.update(id, {
            settings: {
                ..._creditor.settings,
                customExpiration: {
                    ..._creditor.settings.customExpiration,
                    interest: interest,
                },
            },
        });
        q.invalidate(keys.write.modify(creditorsEntity, id));
    };
    const customInterestExpirationUpdater = useMutation(updateCustomInterestExpiration, {
        onError: () => {
            notification({
                title: lang.misc.error,
                theme: 'error',
                subtitle: `${lang.misc.errorUpdating} ${lang.creditors.interestExpiration.toLocaleLowerCase()}`,
            });
        },
    });
    return (React.createElement("div", null, creditor ? (React.createElement(React.Fragment, null,
        React.createElement("div", null,
            React.createElement(SmallHeader, null, lang.creditors.selfCollection),
            React.createElement(Checkbox, { onChange: (e) => {
                    updateSelfCollection(e.target.checked);
                }, checked: state.selfCollection, placeholder: lang.creditors.useSelfCollectionOption, setting: true, size: Size.Small }),
            React.createElement(SmallHeader, null, lang.creditors.outboundCall),
            React.createElement(Checkbox, { onChange: (e) => {
                    updateOutboundCall(e.target.checked);
                }, checked: state.outboundCall, placeholder: lang.creditors.useOutboundCallOption, setting: true, size: Size.Small }),
            React.createElement(SmallHeader, null, lang.creditors.autoVerify),
            React.createElement(Checkbox, { onChange: (e) => {
                    updateAutoVerify(e.target.checked);
                }, checked: state.autoVerify, placeholder: lang.creditors.useAutoVerifyOption, setting: true, size: Size.Small }),
            !creditor.settings.eInvoice?.companyId && (React.createElement(ExclusiveContent, null,
                React.createElement(SmallHeader, null, lang.creditors.eInvoice.eInvoice),
                React.createElement("div", { style: { display: 'inline-flex', width: '100%' } },
                    React.createElement(Select, { value: organizationIdentifier.scheme, onChange: (e) => setOrganizationIdentifier({
                            ...organizationIdentifier,
                            scheme: e.target.value,
                        }), style: { width: '150px' } }, Object.values(OrganizationIdentifierScheme).map((oId) => (React.createElement("option", { key: oId, value: oId }, oId)))),
                    React.createElement(Input, { containerStyle: { width: '100%' }, placeholder: lang.creditors.eInvoice.identification, value: organizationIdentifier.value, onChange: (e) => setOrganizationIdentifier({
                            ...organizationIdentifier,
                            value: e.target.value,
                        }), button: {
                            value: lang.creditors.eInvoice.addChildCompany,
                            loading: loadingAddChildCompany,
                            disabled: !organizationIdentifier.value,
                            onClick: async () => {
                                setLoadingAddChildCompany(true);
                                try {
                                    await creditorsAPI.addChildCompanyToSproom(creditor.creditorId, organizationIdentifier);
                                    q.invalidate(keys.write.modify(creditorsEntity, id));
                                }
                                catch (err) {
                                    alert(lang.misc.error + '. ' + lang.misc.contactSupport);
                                }
                                setLoadingAddChildCompany(false);
                            },
                        } })))),
            creditor.settings.eInvoice?.companyId && (React.createElement(React.Fragment, null,
                React.createElement(SmallHeader, null, lang.creditors.eInvoice.eInvoice),
                React.createElement("div", { style: { display: 'inline-flex', width: '100%' } },
                    React.createElement(Input, { containerStyle: { width: '100%' }, disabled: true, placeholder: lang.creditors.eInvoice.companyId, value: creditor.settings.eInvoice?.companyId, button: me && exclusiveContent?.includes(me.id)
                            ? {
                                value: 'X',
                                loading: loadingRemoveChildCompany,
                                onClick: async () => {
                                    if (confirm('Er du sikker på at du vil fjerne sproom childCompany?')) {
                                        setLoadingRemoveChildCompany(true);
                                        await creditorsAPI.removeChildCompanyFromSproom(id);
                                        q.invalidate(keys.write.modify(creditorsEntity, id));
                                        setLoadingRemoveChildCompany(false);
                                    }
                                },
                            }
                            : undefined })))),
            React.createElement("div", null,
                React.createElement(SmallHeader, null, lang.creditors.normalExpiration),
                React.createElement(Input, { type: "number", value: state.customExpiration?.normal, step: 1, min: 1, placeholder: 'Antal år', onChange: (e) => {
                        setState({
                            ...state,
                            customExpiration: {
                                ...state.customExpiration,
                                normal: parseInt(e.target.value),
                            },
                        });
                    }, button: {
                        loading: customExpirationNormalUpdater.isLoading,
                        value: customExpirationNormalUpdater.recentMutation
                            ? lang.misc.saved
                            : lang.misc.save,
                        onClick: () => {
                            const num = state.customExpiration?.normal;
                            customExpirationNormalUpdater.mutate(creditor, num);
                        },
                    } })),
            React.createElement("div", null,
                React.createElement(SmallHeader, null, lang.creditors.foundationExpiration),
                React.createElement(Input, { type: "number", value: state.customExpiration?.foundation, step: 1, min: 1, placeholder: 'Antal år', onChange: (e) => {
                        setState({
                            ...state,
                            customExpiration: {
                                ...state.customExpiration,
                                foundation: parseInt(e.target.value),
                            },
                        });
                    }, button: {
                        loading: customExpirationFoundationUpdater.isLoading,
                        value: customExpirationFoundationUpdater.recentMutation
                            ? lang.misc.saved
                            : lang.misc.save,
                        onClick: () => {
                            const num = state.customExpiration?.foundation;
                            customExpirationFoundationUpdater.mutate(creditor, num);
                        },
                    } })),
            React.createElement("div", null,
                React.createElement(SmallHeader, null, lang.creditors.interestExpiration),
                React.createElement(Input, { type: "number", value: state.customExpiration?.interest, step: 1, min: 1, placeholder: 'Antal år', onChange: (e) => {
                        setState({
                            ...state,
                            customExpiration: {
                                ...state.customExpiration,
                                interest: parseInt(e.target.value),
                            },
                        });
                    }, button: {
                        loading: customInterestExpirationUpdater.isLoading,
                        value: customInterestExpirationUpdater.recentMutation
                            ? lang.misc.saved
                            : lang.misc.save,
                        onClick: () => {
                            const num = state.customExpiration?.interest;
                            customInterestExpirationUpdater.mutate(creditor, num);
                        },
                    } }))))) : (React.createElement(PlaceholderLoader, null))));
};
export default CreditorConfig;
//# sourceMappingURL=CreditorConfig.js.map